import _extends from "@babel/runtime/helpers/extends";
/*
 *                     NOTICE
 *            COPYRIGHT 2018 Ab Initio
 *       UNPUBLISHED -- ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { AbDialog } from "abreact/popup/AbDialog";
import { PopupManager } from "abreact/popup/PopupManager";
import { AbAlert, AlertLevel } from "absdk/commons/ui/popup/AbAlert";
import * as AbWebResources from "abweb/generated/locale/AbWebResources";
import { KeyValue } from "abweb/utils/KeyboardUtils";
import * as React from "react";

/**
 * The properties for an AbValuePicker's view.
 */

/**
 * An interface for classes that implement value pickers.
 */

/**
 * The interface for a multiple value picker.
 */

/**
 * A class to let users choose a value from a list of possibilities.
 */
export class AbValuePicker {
  /**
   * Shows a modal to let the user pick a value.
   *
   * @param {IValuePicker<V>} valuePicker The value picker implementation.
   * @param {AbValuePickerViewProps} props The properties to configure the modal.
   * @returns {Promise<V>} A promise returning the value.
   */
  pickValue(valuePicker, props) {
    return new Promise(function (resolve, reject) {
      let popupHandle;
      const onValueSelected = () => {
        const value = valuePicker.value;
        if (value !== undefined || props.allowUndefinedSelection) {
          popupHandle.close();
          resolve(value);
          return true;
        } else {
          AbAlert.show({
            level: AlertLevel.ERROR,
            message: AbWebResources.required()
          });
          return false;
        }
      };
      props.autoAccept && valuePicker.observe && valuePicker.observe(onValueSelected);
      props.registerOnValueSelectedCallback && props.registerOnValueSelectedCallback(onValueSelected);
      popupHandle = PopupManager.show(/*#__PURE__*/React.createElement(AbDialog, _extends({
        dataTestId: "value-picker-dialog",
        okButton: !props.autoAccept,
        onOk: () => {
          onValueSelected();
          return Promise.resolve(false);
        },
        cancelButton: true,
        onCancel: () => {
          popupHandle.close();
          reject(undefined);
          return Promise.resolve(false);
        }
      }, props, {
        onKeyDown: event => {
          if (props.onKeyDown && props.onKeyDown(event)) {
            return true;
          } else if (!props.autoAccept && event.key === KeyValue.ENTER) {
            // I will be honest, I don't know how to property document this, but I'll at least try to
            // explain why the if() statement above is the way it is (or, was, when I wrote this). We
            // discovered that reference property item's browse dialogs were hitting this code when
            // the user hit ENTER in, for example, a search box. This caused a "Field is required"
            // message - totally confusing and wrong - or it would dismiss the dialog - also totally
            // wrong. So, I was encouraged to incorporate the !props.autoAccept check here in order to
            // prevent the browse dialog from going through this.
            return onValueSelected();
          }
          return false;
        }
      }), props.view));
    });
  }

  /**
   * Shows a modal to let the user pick one or more values.
   *
   * @param {IValuePicker<V>} valuePicker The value picker implementation.
   * @param {AbValuePickerViewProps} props The properties to configure the modal.
   * @returns {Promise<V>} A promise returning the chosen values.
   */
  pickValues(valuePicker, props) {
    return new Promise(function (resolve, reject) {
      let popupHandle;
      popupHandle = PopupManager.show(/*#__PURE__*/React.createElement(AbDialog, _extends({
        dataTestId: "value-picker-dialog",
        okButton: true,
        onOk: async () => {
          popupHandle.close();
          resolve(valuePicker.values);
          return false;
        },
        cancelButton: true,
        onCancel: async () => {
          reject(undefined);
          return true;
        }
      }, props), props.view));
    });
  }
}
//# sourceMappingURL=AbValuePicker.js.map
